/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, PlayIcon } from '@heroicons/react/24/outline';
import { Container } from '../../types';

/** True when a pod's failure reason is an out-of-memory kill (exit code 137). */
export const isOOMFailure = (reason?: string): boolean =>
  !!reason && /oomkilled|exit code 137/i.test(reason);

interface RelaunchPodModalProps {
  isOpen: boolean;
  pod: Container | null;
  isPending: boolean;
  onClose: () => void;
  /** memoryGb is undefined when relaunching with the unchanged configuration. */
  onRelaunch: (memoryGb?: number) => void;
}

const MAX_MEMORY_GB = 1024;

/**
 * Confirmation shown when relaunching an OOM-killed pod: the same memory
 * limit will likely fail again, so it suggests a bumped size (default 2x)
 * while still allowing an as-is relaunch.
 */
const RelaunchPodModal: React.FC<RelaunchPodModalProps> = ({
  isOpen,
  pod,
  isPending,
  onClose,
  onRelaunch,
}) => {
  const currentMemory = pod?.memory_gb ?? 0;
  const suggestedMemory = Math.min(Math.max(Math.ceil(currentMemory * 2), 1), MAX_MEMORY_GB);
  const [rawMemory, setRawMemory] = useState(String(suggestedMemory));

  useEffect(() => {
    if (isOpen) setRawMemory(String(suggestedMemory));
  }, [isOpen, suggestedMemory]);

  if (!isOpen || !pod) return null;

  const parsedMemory = Number(rawMemory);
  const memoryValid =
    Number.isFinite(parsedMemory) && parsedMemory >= 1 && parsedMemory <= MAX_MEMORY_GB;
  const memoryChanged = memoryValid && parsedMemory !== currentMemory;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-white">Pod ran out of memory</h3>
        </div>

        <p className="text-gray-400 mb-4">
          <span className="font-semibold text-white">"{pod.name}"</span> was killed for exceeding
          its <span className="font-semibold text-white">{currentMemory} GB</span> memory limit
          {pod.failure_reason ? (
            <span className="block mt-1 text-xs text-red-400">{pod.failure_reason}</span>
          ) : null}
        </p>

        <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-400">
            Relaunching with the same limit will likely fail again. We suggest increasing the
            memory before relaunching.
          </p>
        </div>

        <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="relaunch-memory">
          New memory limit (GB)
        </label>
        <input
          id="relaunch-memory"
          type="number"
          min={1}
          max={MAX_MEMORY_GB}
          step={1}
          value={rawMemory}
          onChange={(e) => setRawMemory(e.target.value)}
          className="input w-full mb-1"
        />
        <p className="text-xs text-gray-500 mb-6">
          Currently {currentMemory} GB · suggested {suggestedMemory} GB. Billing adjusts to the
          new size from the next billing period.
        </p>

        <div className="flex flex-wrap justify-end gap-3">
          <button onClick={onClose} disabled={isPending} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => onRelaunch(undefined)}
            disabled={isPending}
            className="btn-secondary flex items-center gap-2"
          >
            Relaunch as-is
          </button>
          <button
            onClick={() => onRelaunch(parsedMemory)}
            disabled={isPending || !memoryValid || !memoryChanged}
            className="btn-primary flex items-center gap-2"
          >
            {isPending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
            {memoryValid ? `Relaunch with ${parsedMemory} GB` : 'Relaunch'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelaunchPodModal;
